import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { DashboardConfigPanelComponent } from '../dashboard-config-panel/dashboard-config-panel.component';
import { DashboardConfig, DashboardSectionKey } from '../../models/dashboard-config.model';

const MAX_RANGE_MONTHS = 12;

@Component({
  selector: 'app-dashboard-toolbar',
  imports: [TranslatePipe, MatIcon, DashboardConfigPanelComponent],
  templateUrl: './dashboard-toolbar.component.html',
  styleUrl: './dashboard-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardToolbarComponent {
  username = input<string>('');
  isDefault = input<boolean>(true);
  von = input<string | null>(null);
  bis = input<string | null>(null);
  configOpen = input<boolean>(false);
  config = input<DashboardConfig | null>(null);

  vonChange = output<string>();
  bisChange = output<string>();
  toggleConfig = output<void>();
  sectionToggle = output<DashboardSectionKey>();
  resetToDefault = output<void>();
  closeConfig = output<void>();

  protected readonly rangeError = signal<string | null>(null);

  protected readonly viewLabelKey = computed(() =>
    this.isDefault() ? 'liveDashboard.standardView' : 'liveDashboard.ownView',
  );

  protected onVonInput(value: string): void {
    const bis = this.bis();
    if (bis && this.compare(value, bis) > 0) {
      this.emitBoth(value);
      return;
    }
    if (this.isRangeValid(value, bis)) this.vonChange.emit(value);
  }

  protected onBisInput(value: string): void {
    const von = this.von();
    if (von && this.compare(value, von) < 0) {
      this.emitBoth(value);
      return;
    }
    if (this.isRangeValid(von, value)) this.bisChange.emit(value);
  }

  private emitBoth(value: string): void {
    this.rangeError.set(null);
    this.vonChange.emit(value);
    this.bisChange.emit(value);
  }

  private compare(a: string, b: string): number {
    return new Date(a).getTime() - new Date(b).getTime();
  }

  protected onToggleConfig(): void {
    this.toggleConfig.emit();
  }

  protected onSectionToggle(key: DashboardSectionKey): void {
    this.sectionToggle.emit(key);
  }

  protected onResetToDefault(): void {
    this.resetToDefault.emit();
  }

  protected onCloseConfig(): void {
    this.closeConfig.emit();
  }

  private isRangeValid(von: string | null, bis: string | null): boolean {
    this.rangeError.set(null);
    if (!von || !bis) return true;

    const from = new Date(von);
    const to = new Date(bis);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return true;

    if (this.exceedsMaxMonths(from, to)) {
      this.rangeError.set('liveDashboard.errors.rangeTooLong');
      return false;
    }

    return true;
  }

  private exceedsMaxMonths(from: Date, to: Date): boolean {
    const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (months > MAX_RANGE_MONTHS) return true;
    if (months === MAX_RANGE_MONTHS) return to.getDate() > from.getDate();
    return false;
  }
}
